import { useState } from 'react';
import { isOutdoorSeason } from '../../utils/marketStatus';
import styles from './IndoorSeasonNotice.module.css';

/** sessionStorage key — session-scoped so the notice returns on a fresh visit. */
export const INDOOR_NOTICE_DISMISSED_KEY = 'market_map_indoor_notice_dismissed';

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(INDOOR_NOTICE_DISMISSED_KEY) === '1';
  } catch {
    // Storage can throw (private mode, disabled). Fail open: show the notice.
    return false;
  }
}

function writeDismissed(): void {
  try {
    window.sessionStorage.setItem(INDOOR_NOTICE_DISMISSED_KEY, '1');
  } catch {
    // Best effort — the in-memory dismissal still applies for this mount.
  }
}

interface Props {
  /** Navigate to the Info tab in-app (router push, no full reload). */
  onInfoClick: () => void;
  /** Injectable clock for tests; defaults to the current date. */
  now?: Date;
}

/**
 * Seasonal notice over the map. November–April the market is indoors at
 * the Troy Atrium, but the map raster always shows the summer Monument
 * Square layout — without a hint, off-season visitors land on a map of a
 * venue where the market is not happening. Dismissal persists for the
 * session (sessionStorage) so it doesn't nag on every tab switch.
 */
export function IndoorSeasonNotice({ onInfoClick, now }: Props) {
  const [dismissed, setDismissed] = useState(readDismissed);

  if (dismissed || isOutdoorSeason(now ?? new Date())) return null;

  const handleDismiss = () => {
    writeDismissed();
    setDismissed(true);
    // The dismiss button unmounts with the notice, stranding keyboard focus
    // on <body>. Hand it to the always-mounted map region instead.
    document.querySelector<HTMLElement>('.map-view')?.focus();
  };

  return (
    <div className={styles.root} role="status">
      <p className={styles.text}>
        We're indoors at the Troy Atrium for the winter, November through April. This map shows our
        summer layout.{' '}
        <a
          className={styles.infoLink}
          href="/info"
          onClick={(e) => {
            e.preventDefault();
            onInfoClick();
          }}
        >
          Market info
        </a>
      </p>
      <button
        type="button"
        className={styles.dismissBtn}
        onClick={handleDismiss}
        aria-label="Dismiss indoor market notice"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
          <path
            d="M3 3 L11 11 M11 3 L3 11"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}
